import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

export interface Position {
  x: number;
  y: number;
}

interface DragStart {
  pointerX: number;
  pointerY: number;
  originX: number;
  originY: number;
  started: boolean;
}

// Pointer travel below this is a click, not a drag.
const DRAG_THRESHOLD_PX = 3;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const clampToBounds = (
  { x, y }: Position,
  bounds: Readonly<{ width: number; height: number }>,
  size: Readonly<{ width: number; height: number }>,
): Position => ({
  x: clamp(x, 0, Math.max(0, bounds.width - size.width)),
  y: clamp(y, 0, Math.max(0, bounds.height - size.height)),
});

type UseDraggableOptions = Readonly<{
  initial: Position;
  /** Positions are measured from, and kept inside, this element. */
  boundsRef: RefObject<HTMLElement | null>;
  /** Measured for clamping. */
  nodeRef: RefObject<HTMLElement | null>;
  onDragStart?: () => void;
  onDragEnd?: (position: Position) => void;
}>;

/** Pointer drag reported on release; a press under the threshold fires neither callback. */
export const useDraggable = ({
  initial,
  boundsRef,
  nodeRef,
  onDragStart,
  onDragEnd,
}: UseDraggableOptions) => {
  const [position, setPosition] = useState<Position>(initial);
  const startRef = useRef<DragStart | null>(null);
  const positionRef = useRef<Position>(initial);

  positionRef.current = position;

  useEffect(() => {
    if (!startRef.current?.started) {
      setPosition(initial);
    }
  }, [initial.x, initial.y]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    startRef.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      originX: positionRef.current.x,
      originY: positionRef.current.y,
      started: false,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const start = startRef.current;

      if (!start) return;

      const dx = e.clientX - start.pointerX;
      const dy = e.clientY - start.pointerY;

      if (!start.started) {
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;

        start.started = true;
        onDragStart?.();
      }

      const bounds = boundsRef.current;
      const node = nodeRef.current;
      const next = { x: start.originX + dx, y: start.originY + dy };

      setPosition(
        bounds && node
          ? clampToBounds(
              next,
              { width: bounds.clientWidth, height: bounds.clientHeight },
              { width: node.offsetWidth, height: node.offsetHeight },
            )
          : next,
      );
    },
    [boundsRef, nodeRef, onDragStart],
  );

  const endDrag = useCallback(
    (e: React.PointerEvent) => {
      const start = startRef.current;

      if (!start) return;

      startRef.current = null;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      if (start.started) onDragEnd?.(positionRef.current);
    },
    [onDragEnd],
  );

  return {
    position,
    dragHandleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
  };
};
